import React, { useRef } from "react";
import "./detailPreliquidationInvoice.scss";
import { Form, FormSpy } from "react-final-form";
import { BasicIcon, Button, Tooltip, ToolTipPosition } from "@pickit/pickit-components";
import { PdfViewer } from "component/pdf-viewer/PdfViewer";
import { InvoiceFormType, detailPreliquidationInvoicePropsType } from "./types";
import useValidationSchema from "hooks/useValidationSchema";
import PdfController from "component/pdfController/PdfController";
import validator from "utils/validator";
import InvoiceDataAR from "./invoiceData/AR/InvoiceDataAR";
import GeneralData from "./generalData/GeneralData";
import InvoiceDataCO from "./invoiceData/CO/InvoiceDataCO";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import "./i18n";
import InvoiceDataMX from "./invoiceData/MX/InvoiceDataMX";
import { LoadingButtonState } from "component/loadingButton/types";
import { CountryComponentType } from "utils/types";
import { PRELIQUIDATION_STATUS, PRELIQUIDATION_STATUS_NAMES } from "utils/constants";
import InvoiceDataCL from "./invoiceData/CL/InvoiceDataCL";

const separatorIndexes = [8, 13, 18, 23];

export const Invoice: React.FC<detailPreliquidationInvoicePropsType> = ({
  invoiceFileStatus,
  presettlementId,
  minDate,
  invoiceDetail,
  detailPreliquidations,
  validationSchema,
  invoiceTypes,
  initialValues,
  isFormEnabled,
  isFormEnabledCL,
  approvingInvoice,
  keepDirty,
  countryCode,
  disableDeleteReplaceIcon,
  disableRejectInvoice,
  isBulkFlow,
  setDirty,
  getInvoiceDetailSave,
  getInvoiceDetailApprove,
  fileHandler,
  deleteFile,
  downloadFile,
  goToPreviousFile,
  toggleRejectInvoiceModalVisibility,
  testId,
}): JSX.Element => {
  const pdfControllerRef = useRef<any>();
  const { t } = useTranslation();
  const isPending = detailPreliquidations.status !== PRELIQUIDATION_STATUS.PENDING;
  const isApprovingInvoice =
    approvingInvoice === LoadingButtonState.Loading;

  const invoiceData: CountryComponentType = {
    AR: (
      <InvoiceDataAR
        isFormEnabled={isFormEnabled}
        minDate={minDate}
        invoiceTypes={invoiceTypes}
        testId={`${testId}-data`}
      />
    ),
    CO: (
      <InvoiceDataCO
        isFormEnabled={isFormEnabled}
        minDate={minDate}
        invoiceTypes={invoiceTypes}
        testId={`${testId}-data`}
      />
    ),
    MX: (
      <InvoiceDataMX
        testId={`${testId}-data`}
        isFormEnabled={isFormEnabled}
        minDate={minDate}
      />
    ),
    CL: (
      <InvoiceDataCL
        testId={`${testId}-data`}
        isFormEnabled={isFormEnabled}
        isFormEnabledCL={isFormEnabledCL}
        minDate={minDate}
        invoiceTypes={invoiceTypes}
      />
    ),
  };

  return (
    <div>
      <Form
        onSubmit={(value) => value}
        initialValues={initialValues}
        keepDirtyOnReinitialize={isBulkFlow ? false : keepDirty}
        mutators={{
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value);
          },
          setMXInvoice: ([field, value], state, { changeValue }) => {
            if (separatorIndexes.includes(value.length)) {
              changeValue(state, field, () => value + "-");
            }
            if (
              separatorIndexes.includes(value.length - 1) &&
              value.slice(-1) !== "-"
            ) {
              const newValue =
                value.slice(0, value.length - 1) + "-" + value.slice(-1);
              changeValue(state, field, () => newValue);
            }
          },
        }}
        validate={useValidationSchema(validationSchema(false))}
      >
        {({ invalid, handleSubmit, form, values, dirty }) => (
          <form className="form-filter-transaction-invoice" onSubmit={handleSubmit}>
            <FormSpy
              subscription={{
                values: true,
                dirty: true,
                invalid: true,
              }}
              onChange={(pro) => {
                setDirty(pro.dirty);
              }}
            />
            <div className="container-detail-preliquidation form-detail-preliquidation">
              <div className="invoice-title-container">
                <h3
                  data-testId={`${testId}-subtitle-pending-data`}
                  className="subTitle-pending-data"
                >
                  {t("invoice:invoiceData")}
                </h3>
                <p
                  data-testId={`${testId}-status`}
                  className="invoice-status-container"
                >
                  {t("invoice:status")}:
                  <span
                    className={classNames("invoice-status", {
                      "invoice-status-approved":
                        detailPreliquidations.status === PRELIQUIDATION_STATUS.APPROVED,
                    })}
                  >
                    {t(
                      `${
                        PRELIQUIDATION_STATUS_NAMES[
                          detailPreliquidations.status
                        ]
                      }`
                    )}
                  </span>
                </p>
              </div>

              <div className="container-detail-preliquidation-row container-invoice-first-row">
                <div
                  data-testId={`${testId}-country-code`}
                  className="container-detail-preliquidation-col-sm-5 form-part-1-admin-pickers detail-preliquidation-padding-right"
                >
                  {invoiceData[countryCode]}
                </div>
                <div className="container-detail-preliquidation-col-sm-2 detail-preliquidation-adjust">
                  <PdfController
                    ref={pdfControllerRef}
                    testId={testId}
                    title={t("invoice:invoice")}
                    buttonText={
                      invoiceDetail.invoiceFile?.upload &&
                      invoiceFileStatus.error
                        ? t("invoice:previousInvoice")
                        : t("invoice:uploadInvoice")
                    }
                    fileUploaded={invoiceDetail.invoiceFile?.upload}
                    showError={invoiceFileStatus.error}
                    errorMessage={t(invoiceFileStatus.message || "")}
                    loading={invoiceFileStatus.loading}
                    fileHandler={fileHandler}
                    disabled={
                      isPending ||
                      isApprovingInvoice ||
                      (!isFormEnabled && !invoiceFileStatus.error && invoiceDetail?.invoiceFile?.upload)
                    }
                    goToPreviousFile={goToPreviousFile}
                  >
                    <PdfViewer src={invoiceDetail.invoiceFile?.url || ""}>
                      <Tooltip
                        message={t("global:delete")}
                        position={ToolTipPosition.top}
                      >
                        <div className={classNames("icon-container-primary", { "disabled": disableDeleteReplaceIcon || isApprovingInvoice })}>
                          <BasicIcon
                            onClick={isApprovingInvoice ? undefined : deleteFile}
                            data-testId={`${testId}-delete-pdf-button`}
                            name="trash"
                            size="small"
                            disabled={disableDeleteReplaceIcon || isApprovingInvoice}
                          />
                        </div>
                      </Tooltip>
                      <Tooltip
                        message={t("global:replace")}
                        position={ToolTipPosition.top}
                      >
                        <div className={classNames("icon-container-primary", { "disabled": disableDeleteReplaceIcon })}>
                          <BasicIcon
                            disabled={disableDeleteReplaceIcon}
                            onClick={
                              isApprovingInvoice
                                ? undefined
                                : () => pdfControllerRef.current?.triggerOnChange()
                            }
                            data-testId={`${testId}-replace-pdf-button`}
                            name="switch-vertical"
                            size="small"
                          />
                        </div>
                      </Tooltip>
                      <Tooltip
                        message={t("global:download")}
                        position={ToolTipPosition.top}
                      >
                        <div className={classNames("icon-container-secondary", { "disabled": disableDeleteReplaceIcon })}>
                          <BasicIcon
                            disabled={!invoiceDetail.invoiceFile?.upload}
                            name="download"
                            color="#FFFFFF"
                            size="small"
                            data-testId={`${testId}-download-pdf-button`}
                            onClick={isApprovingInvoice ? undefined : downloadFile}
                          />
                        </div>
                      </Tooltip>
                    </PdfViewer>
                  </PdfController>
                </div>
              </div>
              <h3 className="subTitle-pending-data detail-preliquidation-margin-top" data-test-id={`${testId}-general-data-title`}>
                {t("invoice:generalData")}
              </h3>
              <div className="form-part-1-admin-pickers detail-preliquidation-margin-top">
                <GeneralData
                  testId="general-data"
                  pickerId={detailPreliquidations.pickerId}
                  countryCode={countryCode}
                />
              </div>
            </div>
            <div className="detail-preliquidation-buttons">
              <Button
                disabled={!isFormEnabled || !dirty || isApprovingInvoice}
                testId={`${testId}-submit-button`}
                label={t("global:save")}
                size="medium"
                width="220"
                hierarchy="primary"
                onClick={() =>
                  validator({
                    values,
                    validationSchema: validationSchema(true),
                    onClick: () =>
                      getInvoiceDetailSave({
                        ...(values as InvoiceFormType),
                        presettlementId,
                      }),
                  })
                }
              />
              <div className="detail-preliquidation-fondo">
                <Button
                  testId={`${testId}-reject-invoice-button`}
                  label={t("rejectInvoice:reject")}
                  size="medium"
                  width="220"
                  hierarchy="secondary"
                  disabled={
                    isApprovingInvoice ||
                    disableRejectInvoice(
                      detailPreliquidations.status !== PRELIQUIDATION_STATUS.IN_APPROVEMENT && invalid
                    )
                  }
                  onClick={() => toggleRejectInvoiceModalVisibility(true)}
                />
                <Tooltip
                  message={t("invoice:missingDataTooltip")}
                  position={ToolTipPosition.top}
                  disabled={
                    !!initialValues.fiscalData.fiscalNumber ||
                    detailPreliquidations.status !== PRELIQUIDATION_STATUS.IN_APPROVEMENT
                  }
                >
                  <Button
                    testId={`${testId}-approve-button`}
                    label={t("invoice:approve")}
                    size="medium"
                    width="220"
                    hierarchy="primary"
                    disabled={
                      isApprovingInvoice ||
                      invoiceFileStatus.error ||
                      invalid ||
                      !invoiceDetail.invoiceFile?.upload ||
                      detailPreliquidations.status !== PRELIQUIDATION_STATUS.IN_APPROVEMENT ||
                      !initialValues.fiscalData.fiscalNumber
                    }
                    onClick={() =>
                      getInvoiceDetailApprove({
                        ...(values as InvoiceFormType),
                        presettlementId,
                      })
                    }
                    isSubmitting={isApprovingInvoice}
                  />
                </Tooltip>
              </div>
            </div>
          </form>
        )}
      </Form>
    </div>
  );
};

export default Invoice;

